import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { renderPartnerLogo, partnersContent } from './utils';

const SuccessPartnersSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Success Partners
  const successPartners = [
    {
      id: 1,
      name: 'وزارة الخارجية و المغتربين',
      logo: "/assets/logos/logo.svg",
      logoAlt: "وزارة الخارجية و المغتربين logo",
      website: ""
    },
    {
      id: 2,
      name: 'شؤون الطلاب',
      logo: "/assets/logos/youth.png",
      logoAlt: "Youth logo",
      website: ""
    },
    {
      id: 3,
      name: "الهيئات الطلابية",
      logo: "/assets/logos/tulabia.png",
      logoAlt: "الهيئات الطلابية logo",
      website: ""
    }
  ];

  const content = partnersContent?.[currentLanguage];

  return (
    <section id="success-partners" className="relative py-16 lg:py-24 px-4 bg-gradient-to-b from-[#114845]/5 via-[#114845]/3 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-24 lg:mb-32"
        >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-success/10 rounded-full blur-lg"></div>
              <div className="relative w-12 h-12 bg-gradient-to-br from-success/20 to-primary/20 rounded-full flex items-center justify-center border border-success/30 shadow-memorial">
                <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <h3 className="font-memorial text-2xl md:text-3xl lg:text-4xl font-bold mb-6" dir="rtl">
            <span className="bg-gradient-to-r from-success via-primary to-accent bg-clip-text text-transparent drop-shadow-sm">
              داعموا المسيرة وشركاء الحدث
            </span>
          </h3>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="font-body text-lg md:text-xl text-text-primary leading-relaxed max-w-3xl mx-auto mb-8" dir="rtl"
          >
            لم يكن لهذا الحدث أن يخرج بهذه الصورة المشرفة لولا تكاتفنا مع شركاء استراتيجيين، قدموا الدعم والرعاية إيماناً منهم بأهمية الصوت الطلابي الحر، وحرصاً على تمكين الشباب في مختلف الميادين.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex items-center justify-center gap-2"
          >
            <div className="h-1 w-12 bg-gradient-to-r from-transparent to-success rounded-full"></div>
            <div className="h-1 w-2 bg-success rounded-full"></div>
            <div className="h-1 w-12 bg-gradient-to-l from-transparent to-success rounded-full"></div>
          </motion.div>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-center justify-items-center">
          {successPartners.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="bg-background border-2 border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 w-full text-center"
            >
              {item.logo && item.logo !== "" ? (
                <>
                  <div className={`flex items-center justify-center ${item.isLarge ? 'h-48' : 'h-40'}`}>
                    {renderPartnerLogo(item)}
                  </div>
                  <p className="text-base md:text-2xl font-medium text-gray-800 mt-3">
                    {item.name}
                  </p>
                </>
              ) : (
                <div className="text-lg md:text-xl">{item.name}</div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
      </div>
    </section>
  );
};

export default SuccessPartnersSection;

